/**
 * Renderer-side screenshot auto-resize (issue #212).
 *
 * Background. The screenshot-paste MVP (#194 / alpha.53) shipped these
 * caps, enforced both renderer-side and (authoritative) main-side via
 * `validateAttachments`:
 *
 *   - 5 MB per image (decoded byte length of the base64 payload)
 *   - 10 MB per message total
 *   - MIME types: image/jpeg | image/png | image/gif | image/webp
 *
 * Real-user repro (issue #212): a Mac retina screenshot at 2880×1656
 * weighs ~4-5 MB raw, sails through our 5 MB cap, and then Anthropic's
 * vision API kicks it back server-side. The agent's response landed
 * as "I couldn't see it as it was too large" — no actionable next step.
 *
 * The fix has two layers:
 *
 *   1. Renderer-side downscale before encoding. If the image is over
 *      `RESIZE_LONGEST_SIDE_PX` on the longest axis OR over
 *      `RESIZE_RAW_BYTE_THRESHOLD` raw bytes, shrink it via `<canvas>`
 *      to the target ceiling, preserving aspect ratio and MIME (PNG
 *      stays PNG, JPEG stays JPEG with quality 0.9, WebP stays WebP).
 *      A 1920px-wide screenshot is plenty for any UI / log capture
 *      and well under Anthropic's recommended 1568px cost-efficiency
 *      ceiling.
 *
 *   2. Better error surfacing when the model rejects an attachment for
 *      size or dimension reasons even after our resize. That branch
 *      lives in `agent-errors.ts` — see the `vision_too_large` /
 *      `image too large` classifier added alongside this module.
 *
 * Design notes:
 *
 *   - `decideResize` is pure: it takes width, height, MIME, byte length
 *      and the threshold options, returns a typed decision. This is the
 *      load-bearing business logic and lives behind 100% unit-test
 *      coverage in `__tests__/resize-image.test.ts`.
 *
 *   - `resizeImageFile` is the browser-only wrapper that actually
 *      drives `<canvas>` + `Image()` + `toBlob`. It's hard to unit-test
 *      under vitest's `node` environment (no DOM, no canvas), so we
 *      keep it minimal — every decision lives in `decideResize`, the
 *      wrapper just executes.
 *
 *   - Main-side `validateAttachments` is unchanged: it stays
 *      authoritative on byte length / MIME, this is purely a UX-layer
 *      shim. If the renderer is ever bypassed, the cap still holds.
 */

/**
 * Longest-side ceiling we resize down to. 1920 px keeps the image
 * useful for any UI / console screenshot (Macs default to 1440-2880,
 * Windows displays vary 1366-3840) while staying well below
 * Anthropic's documented 8000 px hard cap and a comfortable margin
 * over their recommended 1568 px cost-efficiency target.
 *
 * Trade-off note: a more aggressive 1568 px cap would shave a few
 * cents per send but would slightly degrade text-readability on
 * 4K-density console captures. Issue #212 was a usability bug, not a
 * cost optimisation, so we err toward usable.
 */
export const RESIZE_LONGEST_SIDE_PX = 1920;

/**
 * Raw-byte threshold above which we resize even when dimensions
 * already fit under the longest-side ceiling. 1.5 MB catches highly
 * compressed PNGs that happen to be small dimensionally but heavy
 * (e.g. high-density screenshots of code with anti-aliased fonts) and
 * keeps a comfortable margin under both our 5 MB per-image cap and
 * Anthropic's per-image processing cost curve.
 */
export const RESIZE_RAW_BYTE_THRESHOLD = 1.5 * 1024 * 1024;

/**
 * Re-encode quality for lossy formats (JPEG, WebP) when we have to
 * write a new blob. 0.9 is the standard-issue balance between visible
 * artefacting and meaningful byte savings; PNG ignores this (lossless).
 */
export const RESIZE_REENCODE_QUALITY = 0.9;

/**
 * Floor on the scale factor used when ONLY the byte threshold tripped
 * (image dimensions already fit under the longest-side cap, but the
 * raw payload is still over `RESIZE_RAW_BYTE_THRESHOLD`). Without a
 * floor a 50 MB 100×100 PNG would collapse to a postage stamp; 0.5
 * keeps the longest side at half the original at worst, which still
 * shaves ~75% of the pixel count and is plenty for any UI / log
 * screenshot we'd actually see in this branch.
 */
export const BYTE_ONLY_MIN_SCALE = 0.5;

/**
 * MIME types that survive a canvas re-encode round-trip without
 * format swap. Anything outside this set falls back to PNG (the
 * canvas API's default lossless target).
 */
const REENCODE_PRESERVING_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * Pure decision function. Given the source image's dimensions, MIME,
 * and raw byte length, decide whether to resize and (if so) what the
 * target dimensions / MIME / quality should be.
 *
 * Trigger conditions (OR):
 *   - longest side > `longestSidePx`
 *   - raw byte length > `rawByteThreshold`
 *
 * When triggered, the longest side is clamped to `longestSidePx` and
 * the shorter side scales proportionally. Aspect ratio is preserved.
 *
 * MIME is preserved when in `REENCODE_PRESERVING_MIMES`; otherwise
 * we target PNG (canvas's lossless default). GIF specifically falls
 * back to PNG — animated GIFs collapse to a single frame anyway when
 * drawn to a canvas, and a static PNG of the first frame is the
 * least-surprising outcome.
 */
export interface ResizeDecision {
  shouldResize: boolean;
  /** Target output width in CSS pixels. Equals input width when
   *  `shouldResize === false`. */
  targetWidth: number;
  /** Target output height in CSS pixels. Equals input height when
   *  `shouldResize === false`. */
  targetHeight: number;
  /** Output MIME — same as input for the supported set, PNG otherwise. */
  targetMime: string;
  /** Re-encode quality for lossy formats (JPEG / WebP). Ignored by PNG. */
  quality: number;
  /** Why we resized — surfaced in the chip label so the user knows. */
  reason: 'dimensions' | 'bytes' | 'both' | 'none';
}

export interface ResizeOptions {
  longestSidePx?: number;
  rawByteThreshold?: number;
  quality?: number;
}

export function decideResize(
  width: number,
  height: number,
  mimeType: string,
  byteLength: number,
  opts: ResizeOptions = {},
): ResizeDecision {
  const longestSidePx = opts.longestSidePx ?? RESIZE_LONGEST_SIDE_PX;
  const rawByteThreshold = opts.rawByteThreshold ?? RESIZE_RAW_BYTE_THRESHOLD;
  const quality = opts.quality ?? RESIZE_REENCODE_QUALITY;

  // Defensive: a zero / negative dimension means we couldn't decode
  // the image's metadata. Don't try to resize — the caller will fall
  // through to the original bytes and the main-side cap will catch
  // anything genuinely oversized.
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return {
      shouldResize: false,
      targetWidth: width,
      targetHeight: height,
      targetMime: REENCODE_PRESERVING_MIMES.has(mimeType) ? mimeType : 'image/png',
      quality,
      reason: 'none',
    };
  }

  const longest = Math.max(width, height);
  const overDimensions = longest > longestSidePx;
  const overBytes = byteLength > rawByteThreshold;

  const targetMime = REENCODE_PRESERVING_MIMES.has(mimeType) ? mimeType : 'image/png';

  if (!overDimensions && !overBytes) {
    return {
      shouldResize: false,
      targetWidth: width,
      targetHeight: height,
      targetMime,
      quality,
      reason: 'none',
    };
  }

  // Pick a scale factor that brings both axes down to thresholds.
  //
  // Two scales contribute:
  //   - `dimScale`: shrinks the longest side to `longestSidePx` when
  //     it's over the cap. Trivially 1 when dimensions already fit.
  //   - `byteScale`: pixel-count-proportional shrink to bring the
  //     re-encoded bytes down toward `rawByteThreshold` (image bytes
  //     scale roughly with pixel count, so target pixel count is
  //     1/overshoot of the original; the side-length scale is sqrt).
  //
  // Apply the SMALLER of the two when both triggers fire — Codex
  // round-2 review on PR #230 caught that the previous if/else-if
  // shape used dimScale alone whenever dimensions tripped, even if
  // the byte overshoot wanted a more aggressive shrink. A 2000×1000
  // image at e.g. 5 MB would shrink to 1920×960 by dimensions only
  // and still bust the upstream image cap.
  //
  // Round-1 review caught the prior `Math.min(1, longestSidePx /
  // longest)` for the byte-only case — when `longest <=
  // longestSidePx`, that ratio is always ≥ 1 so the min always
  // returned 1, and the byte-only path never actually scaled.
  //
  // The `BYTE_ONLY_MIN_SCALE` floor (Codex round-3 catch) only
  // applies when dimensions DON'T overflow. The floor exists to
  // stop a pathological 50 MB 100×100 image from collapsing to a
  // postage stamp — that's specifically a byte-only-trigger
  // protection. When dimensions also overflow, the user has already
  // shown they're working with a high-res capture; clamping the
  // byte scale at 0.5 there can leave a 5+ MB output that's still
  // over the upstream image cap.
  const dimScale = overDimensions ? longestSidePx / longest : 1;
  let byteScale = 1;
  if (overBytes) {
    const overshoot = byteLength / rawByteThreshold;
    const raw = Math.sqrt(1 / overshoot);
    byteScale = Math.min(1, raw);
    // Floor applies only in the byte-only branch (dimensions fit).
    if (!overDimensions) {
      byteScale = Math.max(BYTE_ONLY_MIN_SCALE, byteScale);
    }
  }
  const scale = Math.min(dimScale, byteScale);

  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const reason: ResizeDecision['reason'] =
    overDimensions && overBytes ? 'both' : overDimensions ? 'dimensions' : 'bytes';

  return {
    shouldResize: true,
    targetWidth,
    targetHeight,
    targetMime,
    quality,
    reason,
  };
}

/**
 * Result of the browser-side resize wrapper. `didResize` is the truthy
 * signal the renderer uses to decide whether to flag the chip with a
 * "resized 2880x1656 -> 1920x1106" label.
 *
 * `originalWidth` / `originalHeight` are the source image's actual
 * decoded dimensions (read off the `Image` element after `onload`),
 * captured even when no resize happened so the chip can still surface
 * an informational label if we ever want to show "1280x720" for
 * raw-pass-through images.
 */
export interface ResizeResult {
  didResize: boolean;
  base64: string;
  mimeType: string;
  byteLength: number;
  originalWidth: number;
  originalHeight: number;
  finalWidth: number;
  finalHeight: number;
}

/**
 * Browser-side wrapper. Reads the file's natural dimensions via an
 * `Image` element, runs the pure `decideResize` to pick the target
 * shape, and (when triggered) draws to an off-screen `<canvas>` and
 * reads the bytes back out via `toBlob` + `FileReader`.
 *
 * Falls back to passing the original file through unchanged on any
 * decode / encode failure — the main-side `validateAttachments` cap
 * is still authoritative, so the worst-case is the user gets the
 * pre-fix behaviour for that one paste.
 *
 * Browser-only — depends on `Image`, `URL.createObjectURL`,
 * `<canvas>`, `toBlob`, `FileReader`. Vitest's `node` environment
 * doesn't expose these, so this function isn't unit-tested
 * directly; the load-bearing decision logic lives in `decideResize`
 * which IS pure and IS tested.
 */
export async function resizeImageFile(file: File, opts: ResizeOptions = {}): Promise<ResizeResult> {
  // Codex / Cursor Bugbot review on PR #230: the dimension probe
  // can throw when the browser fails to decode the pasted image
  // (e.g. malformed clipboard payload, unsupported colour profile).
  // Pre-fix that rejection bubbled out of `resizeImageFile` and the
  // call site in Workspace.tsx silently dropped the paste, which
  // regressed from the documented "worst case is the pre-fix
  // behaviour for that one paste" guarantee. Catch and fall through
  // to the original-bytes pass-through path so the user always gets
  // a usable attachment back (the main-side cap is still
  // authoritative on byte length).
  let naturalWidth = 0;
  let naturalHeight = 0;
  try {
    const probe = await readImageDimensions(file);
    naturalWidth = probe.naturalWidth;
    naturalHeight = probe.naturalHeight;
  } catch (err) {
    console.warn('[chat] image dimension probe failed; sending original bytes', err);
    const base64 = await readFileAsBase64(file);
    return {
      didResize: false,
      base64,
      mimeType: file.type,
      byteLength: file.size,
      originalWidth: 0,
      originalHeight: 0,
      finalWidth: 0,
      finalHeight: 0,
    };
  }

  const decision = decideResize(naturalWidth, naturalHeight, file.type, file.size, opts);

  if (!decision.shouldResize) {
    // Pass through the original bytes. We still read them via
    // FileReader so the contract on the result type holds (base64
    // payload + accurate byteLength) and the call site doesn't have
    // to fork on the resize / no-resize case.
    const base64 = await readFileAsBase64(file);
    return {
      didResize: false,
      base64,
      mimeType: file.type,
      byteLength: file.size,
      originalWidth: naturalWidth,
      originalHeight: naturalHeight,
      finalWidth: naturalWidth,
      finalHeight: naturalHeight,
    };
  }

  try {
    const blob = await drawToCanvasBlob(
      file,
      decision.targetWidth,
      decision.targetHeight,
      decision.targetMime,
      decision.quality,
    );
    const base64 = await readBlobAsBase64(blob);
    return {
      didResize: true,
      base64,
      mimeType: blob.type || decision.targetMime,
      byteLength: blob.size,
      originalWidth: naturalWidth,
      originalHeight: naturalHeight,
      finalWidth: decision.targetWidth,
      finalHeight: decision.targetHeight,
    };
  } catch (err) {
    // If anything in the canvas pipeline blows up, fall through to
    // the original bytes so the user can still send. The main-side
    // cap catches genuinely oversized payloads.

    console.warn('[chat] image resize failed; falling back to original', err);
    const base64 = await readFileAsBase64(file);
    return {
      didResize: false,
      base64,
      mimeType: file.type,
      byteLength: file.size,
      originalWidth: naturalWidth,
      originalHeight: naturalHeight,
      finalWidth: naturalWidth,
      finalHeight: naturalHeight,
    };
  }
}

// ---------------------------------------------------------------------------
// Browser-side helpers — kept private so the public surface stays tidy.
// ---------------------------------------------------------------------------

function readImageDimensions(file: File): Promise<{ naturalWidth: number; naturalHeight: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode pasted image for dimension probe.'));
    };
    img.src = url;
  });
}

function drawToCanvasBlob(
  file: File,
  targetWidth: number,
  targetHeight: number,
  targetMime: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2D context unavailable.'));
          return;
        }
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas toBlob returned null.'));
              return;
            }
            resolve(blob);
          },
          targetMime,
          quality,
        );
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode pasted image for canvas resize.'));
    };
    img.src = url;
  });
}

function readFileAsBase64(file: File): Promise<string> {
  return readBlobAsBase64(file);
}

function readBlobAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string result.'));
        return;
      }
      // Result is a `data:<mime>;base64,<payload>` URL — strip the
      // prefix so the IPC carries just the base64 payload.
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('FileReader error.'));
    };
    reader.readAsDataURL(blob);
  });
}
